/**
 * BrowserPushControl
 * AIDLC Administrative Backend
 *
 * The "this browser" part of Settings > Push (PUSH-ASK-1, BL-e113d148): one plain status line and, when it can work,
 * one button that asks the browser's permission and subscribes. The browser is asked only from that click.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button } from './Button';
import { readBrowserPushState, turnOnBrowserPush } from '../../services/pushSubscription';
import {
  BROWSER_PUSH_STATUS_TEXT,
  TURN_ON_BUTTON_LABEL,
  canTurnOn,
  turnOnFailureText,
  type BrowserPushState,
} from '../../utils/browserPush';

const READ_FAILED_TEXT = 'The notification status for this browser could not be read. Reload the page to try again.';

export function BrowserPushControl() {
  const [state, setState] = useState<BrowserPushState | null>(null);
  const [turningOn, setTurningOn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setState(await readBrowserPushState());
    } catch (err) {
      console.error('[Settings] Reading this browser\'s push state failed:', err);
      setError(READ_FAILED_TEXT);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const handleTurnOn = async () => {
    setTurningOn(true);
    setError(null);
    try {
      const outcome = await turnOnBrowserPush();
      setError(turnOnFailureText(outcome));
      await refresh();
    } finally {
      setTurningOn(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="browser-push">
      <h5 className="text-xs font-medium text-gray-700">This browser</h5>
      <p className="text-xs text-gray-600" data-testid="browser-push-status" aria-live="polite">
        {state ? BROWSER_PUSH_STATUS_TEXT[state] : 'Checking this browser...'}
      </p>
      {state && canTurnOn(state) && (
        <Button size="md" className="min-h-[44px]" onClick={handleTurnOn} isLoading={turningOn}>
          {TURN_ON_BUTTON_LABEL}
        </Button>
      )}
      {error && (
        <p role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
          {error}
        </p>
      )}
    </div>
  );
}
